import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';

import { useRequestAuditQuery } from '../../hooks/queries/useRequestAuditQuery';
import {
  RequestAuditEntry,
  ImpersonationMode,
} from '../../types/Impersonation';
import { dateFormatting } from '../../utils/dateFormatting';

interface ImpersonationActivitySectionProps {
  requestId: number;
  isAdmin: boolean;
}

const formatModeLabel = (mode: ImpersonationMode): string => {
  switch (mode) {
    case 'read_only':
      return 'Read Only';
    case 'full_access':
      return 'Full Access';
    default:
      return mode;
  }
};

const formatActionLabel = (entry: RequestAuditEntry): string => {
  if (!entry.httpMethod) return 'accessed';
  return 'modified';
};

const formatTimestamp = (dateStr: string): string => {
  const date = dateFormatting.parseInstant(dateStr);
  if (!date) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const clock = () =>
    dateFormatting.format(date, {
      locale: 'en-US',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) ?? '';

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60)
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) {
    return `Yesterday at ${clock()}`;
  }
  if (diffDays < 7) return `${diffDays} days ago`;

  const day =
    dateFormatting.format(date, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? '';
  return `${day} at ${clock()}`;
};

export const camelToReadable = (str: string): string =>
  str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

const formatChangesSummary = (summary: string): string => {
  const fields = summary.split(',').filter(Boolean).map(camelToReadable);
  if (fields.length === 0) return '';
  if (fields.length <= 3) return fields.join(', ');
  return `${fields.slice(0, 3).join(', ')} +${fields.length - 3} more`;
};

export const ImpersonationActivitySection: React.FC<
  ImpersonationActivitySectionProps
> = ({ requestId, isAdmin }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, isLoading, isError } = useRequestAuditQuery(requestId, isAdmin);

  if (!isAdmin) {
    return null;
  }

  const entries = data?.entries ?? [];
  const count = data?.count ?? 0;

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
        accessibilityRole="button"
        accessibilityLabel={`Impersonation Activity${
          count > 0 ? `, ${count} entries` : ''
        }, ${isExpanded ? 'collapse' : 'expand'}`}
      >
        <View style={styles.headerLeft}>
          <Text style={styles.headerIcon}>🛡️</Text>
          <Text style={styles.headerTitle}>
            Impersonation Activity
            {count > 0 && <Text style={styles.countBadge}> ({count})</Text>}
          </Text>
        </View>
        <Text style={styles.chevron}>{isExpanded ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      {isExpanded && (
        <View style={styles.content}>
          {isLoading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color="#6b7280" />
              <Text style={styles.loadingText}>Loading audit entries...</Text>
            </View>
          )}

          {isError && (
            <Text style={styles.errorText}>
              Failed to load impersonation activity.
            </Text>
          )}

          {!isLoading && !isError && entries.length === 0 && (
            <Text style={styles.emptyText}>No impersonation changes</Text>
          )}

          {!isLoading &&
            !isError &&
            entries.map(entry => (
              <View key={entry.auditId} style={styles.entryItem}>
                <View style={styles.entryLine1}>
                  <Text style={styles.entryName}>{entry.impersonatorName}</Text>
                  <Text style={styles.entryAction}>
                    {' '}
                    {formatActionLabel(entry)} as{' '}
                  </Text>
                  <View
                    style={[
                      styles.modeBadge,
                      entry.mode === 'read_only'
                        ? styles.modeBadgeReadOnly
                        : styles.modeBadgeFullAccess,
                    ]}
                  >
                    <Text
                      style={[
                        styles.modeBadgeText,
                        entry.mode === 'read_only'
                          ? styles.modeBadgeTextReadOnly
                          : styles.modeBadgeTextFullAccess,
                      ]}
                    >
                      {formatModeLabel(entry.mode)}
                    </Text>
                  </View>
                </View>
                {entry.changesSummary && (
                  <Text style={styles.entryChanges}>
                    Changed: {formatChangesSummary(entry.changesSummary)}
                  </Text>
                )}
                <Text style={styles.entryTimestamp}>
                  {formatTimestamp(entry.createdAt)}
                </Text>
              </View>
            ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    marginVertical: 12,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerIcon: {
    fontSize: 16,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
  },
  countBadge: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  chevron: {
    fontSize: 12,
    color: '#9ca3af',
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorText: {
    fontSize: 14,
    color: '#dc3545',
    paddingVertical: 4,
  },
  emptyText: {
    fontSize: 14,
    color: '#9ca3af',
    fontStyle: 'italic',
    paddingVertical: 4,
  },
  entryItem: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  entryLine1: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  entryName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  entryAction: {
    fontSize: 14,
    color: '#4b5563',
  },
  modeBadge: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  modeBadgeReadOnly: {
    backgroundColor: '#f3f4f6',
  },
  modeBadgeFullAccess: {
    backgroundColor: '#fff3cd',
  },
  modeBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  modeBadgeTextReadOnly: {
    color: '#6b7280',
  },
  modeBadgeTextFullAccess: {
    color: '#856404',
  },
  entryChanges: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  entryTimestamp: {
    fontSize: 13,
    color: '#9ca3af',
    marginTop: 2,
  },
});
